import { useEffect, useRef, useState } from 'react';
import { Heart, Share2, MessageCircle, Volume2, VolumeX, Play, ExternalLink, UserPlus, UserMinus, X, Send, Crown, Lock } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { fileUrl, formatViews } from '../lib/api';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';

export function ShortsPlayer({ video, active, muted, onToggleMute, onLikeChange }) {
    const { user } = useAuth();
    const videoRef = useRef(null);
    // Latest shared mute preference, read inside the async play() callback so the
    // "audio on" choice carries over when advancing to the next Short without
    // restarting playback on the current one.
    const mutedRef = useRef(muted);
    mutedRef.current = muted;
    const [playing, setPlaying] = useState(false);
    const [liked, setLiked] = useState(video.liked || false);
    const [likes, setLikes] = useState(video.likes || 0);
    const [following, setFollowing] = useState(video.is_following || false);
    const [followBusy, setFollowBusy] = useState(false);
    const [likeBusy, setLikeBusy] = useState(false);
    const [trailerEnded, setTrailerEnded] = useState(false);
    const [canWatch, setCanWatch] = useState(!video.is_premium || !!video.can_watch);
    const [buyBusy, setBuyBusy] = useState(false);
    const gated = video.is_premium && !canWatch;
    const [showComments, setShowComments] = useState(false);
    const [comments, setComments] = useState([]);
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [commentText, setCommentText] = useState('');
    const [commentBusy, setCommentBusy] = useState(false);
    const [commentsCount, setCommentsCount] = useState(video.comments_count || 0);
    const startedAt = useRef(null);
    const trailerTarget = video.trailer_for_video_id
        ? `/video/${video.trailer_for_video_id}`
        : video.trailer_for_series_id ? `/series/${video.trailer_for_series_id}` : null;

    useEffect(() => {
        const el = videoRef.current;
        if (!el) return;
        if (active) {
            setTrailerEnded(false);
            if (video.is_premium && !canWatch) { setPlaying(false); return; }
            startedAt.current = Date.now();
            // Browsers block autoplay-with-sound when it isn't triggered by a direct
            // click (e.g. advancing to the next Short via scroll). Start muted, which is
            // always allowed, then unmute right after playback begins if the user's
            // sound preference is on — this keeps audio working across scroll transitions.
            el.muted = true;
            el.play().then(() => {
                setPlaying(true);
                if (!mutedRef.current) el.muted = false;
            }).catch(() => {});
            // record a view
            api.post(`/videos/${video.video_id}/view`).catch(() => {});
        } else {
            if (startedAt.current) api.post(`/videos/${video.video_id}/watch-progress`, { seconds: (Date.now() - startedAt.current) / 1000 }).catch(() => {});
            startedAt.current = null;
            el.pause();
            el.currentTime = 0;
            setPlaying(false);
        }
    }, [active, video.video_id]);

    useEffect(() => () => {
        if (startedAt.current) api.post(`/videos/${video.video_id}/watch-progress`, { seconds: (Date.now() - startedAt.current) / 1000 }).catch(() => {});
    }, [video.video_id]);

    useEffect(() => {
        setLiked(video.liked || false);
        setLikes(video.likes || 0);
        setFollowing(video.is_following || false);
        setCommentsCount(video.comments_count || 0);
    }, [video.video_id, video.liked, video.likes, video.is_following, video.comments_count]);

    const openComments = async () => {
        setShowComments((value) => !value);
        if (comments.length || commentsLoading) return;
        setCommentsLoading(true);
        try {
            const r = await api.get(`/videos/${video.video_id}/comments`);
            setComments(r.data.items || []);
        } catch {
            // ignore, panel will just show empty state
        } finally {
            setCommentsLoading(false);
        }
    };

    const submitComment = async (e) => {
        e.preventDefault();
        if (!user) {
            toast.error('Inicia sessió per comentar');
            return;
        }
        if (!commentText.trim() || commentBusy) return;
        setCommentBusy(true);
        try {
            const r = await api.post(`/videos/${video.video_id}/comments`, { content: commentText.trim() });
            setComments((cur) => [r.data, ...cur]);
            setCommentsCount((n) => n + 1);
            setCommentText('');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s\'ha pogut publicar el comentari');
        } finally {
            setCommentBusy(false);
        }
    };

    const togglePlay = () => {
        const el = videoRef.current;
        if (!el) return;
        if (el.paused) { el.play(); setPlaying(true); } else { el.pause(); setPlaying(false); }
    };

    const toggleMute = () => {
        const el = videoRef.current;
        if (!el) return;
        // `muted` is the current (pre-toggle) value shared from the feed, so the
        // new state is `!muted`. Flip the element immediately for responsiveness,
        // then let the parent own the persisted preference so every Short stays in
        // sync when scrolling to the next one.
        el.muted = !muted;
        onToggleMute && onToggleMute();
        if (!el.muted) el.play().then(() => setPlaying(true)).catch(() => {});
    };

    const toggleLike = async () => {
        if (!user) {
            toast.error('Inicia sessió per donar M\'agrada');
            return;
        }
        if (likeBusy) return;
        setLikeBusy(true);
        try {
            const r = await api.post(`/videos/${video.video_id}/like`);
            setLiked(r.data.liked);
            setLikes((n) => n + (r.data.liked ? 1 : -1));
            onLikeChange && onLikeChange(r.data.liked);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No s\'ha pogut actualitzar el M\'agrada');
        } finally {
            setLikeBusy(false);
        }
    };

    const toggleFollow = async () => {
        if (!user) {
            toast.error('Inicia sessió per seguir creadors');
            return;
        }
        if (followBusy) return;
        setFollowBusy(true);
        try {
            if (following) {
                await api.delete(`/users/${video.uploader_id}/follow`);
                setFollowing(false);
            } else {
                await api.post(`/users/${video.uploader_id}/follow`);
                setFollowing(true);
            }
        } catch (e) {
            toast.error('No s\'ha pogut actualitzar el seguiment');
        } finally {
            setFollowBusy(false);
        }
    };

    const share = async () => {
        const url = `${window.location.origin}/video/${video.video_id}`;
        try {
            if (navigator.share) await navigator.share({ url, title: video.title });
            else { await navigator.clipboard.writeText(url); toast.success('Enllaç copiat'); }
        } catch {}
    };

    const buyPremium = async () => {
        if (!user) { window.location.href = '/entrar'; return; }
        if (buyBusy) return;
        setBuyBusy(true);
        try {
            const r = await api.post(`/videos/${video.video_id}/create-checkout-session`);
            if (r.data && r.data.url) { window.location.href = r.data.url; return; }
            if (r.data && r.data.mock) {
                toast.success('Compra simulada completada');
                setCanWatch(true);
                return;
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No s\'ha pogut iniciar la compra');
        } finally {
            setBuyBusy(false);
        }
    };

    return (
        <div className="relative h-full w-full bg-black snap-center overflow-hidden" data-testid="shorts-feed-item">
            {!gated && (
                <video
                    ref={videoRef}
                    src={fileUrl(video.video_url)}
                    muted={muted}
                    loop={!trailerTarget}
                    playsInline
                    onEnded={() => { setPlaying(false); setTrailerEnded(true); }}
                    onClick={togglePlay}
                    className="absolute inset-0 h-full w-full object-contain bg-black"
                >
                    {(video.subtitles || []).map((s, index) => (
                        <track key={s.lang} src={s.url} kind="subtitles" srcLang={s.lang} label={s.name} default={index === 0} />
                    ))}
                </video>
            )}

            {!playing && !trailerEnded && (
                <button onClick={togglePlay} className="absolute inset-0 grid place-items-center">
                    <span className="h-16 w-16 rounded-full bg-black/40 grid place-items-center"><Play className="h-8 w-8 text-white fill-current" /></span>
                </button>
            )}

            {trailerEnded && trailerTarget && (
                <div className="absolute inset-0 z-10 grid place-items-center bg-black/75 px-6 text-center">
                    <div className="max-w-sm text-white">
                        <h3 className="font-display text-xl font-semibold">Vols continuar?</h3>
                        <p className="mt-2 text-sm text-white/75">Obre {video.trailer_for_series_id ? 'la sèrie relacionada' : 'el vídeo complet'} publicat pel creador.</p>
                        <button type="button" onClick={() => { window.location.href = trailerTarget; }} className="mt-5 inline-flex items-center gap-2 rounded bg-[color:var(--alb-sun)] px-4 py-2.5 font-semibold text-[color:var(--alb-ink)]" data-testid="shorts-trailer-continue">
                            <ExternalLink className="h-4 w-4" /> Continuar
                        </button>
                    </div>
                </div>
            )}

            {gated && (
                <div className="absolute inset-0 z-10" data-testid="shorts-premium-gate">
                    {video.thumbnail_url && <img src={fileUrl(video.thumbnail_url)} alt="" className="absolute inset-0 h-full w-full object-cover blur-2xl opacity-40" />}
                    <div className="absolute inset-0 bg-black/60" />
                    <div className="relative z-10 grid h-full place-items-center px-6 text-center">
                        <div className="max-w-sm text-white">
                            <span className="inline-flex items-center gap-1 rounded-full bg-[color:var(--alb-sun)] px-3 py-1 text-xs font-bold uppercase tracking-wide text-[color:var(--alb-ink)]"><Crown className="h-3.5 w-3.5" /> Premium</span>
                            <h3 className="font-display mt-3 text-xl font-semibold">{video.title}</h3>
                            <p className="mt-2 text-sm text-white/75">Aquest short és de pagament. Compra l'accés per veure'l.</p>
                            {video.price != null && <p className="mt-1 font-mono-tab text-[color:var(--alb-sun)]">{Number(video.price).toFixed(2)} €</p>}
                            <button type="button" onClick={buyPremium} disabled={buyBusy} className="mt-5 inline-flex items-center gap-2 rounded bg-[color:var(--alb-sun)] px-4 py-2.5 font-semibold text-[color:var(--alb-ink)] disabled:opacity-50" data-testid="shorts-premium-buy">
                                <Lock className="h-4 w-4" /> Comprar accés
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {!gated && video.is_premium && (
                <span className="absolute left-3 top-3 z-10 inline-flex items-center gap-1 rounded-full bg-[color:var(--alb-sun)] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-[color:var(--alb-ink)]"><Crown className="h-3 w-3" /> Premium</span>
            )}

            <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.75) 100%)' }} />

            <div className="absolute right-3 bottom-24 flex flex-col items-center gap-4">
                <button data-testid="shorts-like-button" onClick={toggleLike} disabled={likeBusy} className="flex flex-col items-center text-white">
                    <span className={`h-12 w-12 grid place-items-center rounded-full bg-black/40 backdrop-blur ${liked ? 'text-[color:var(--alb-pink)]' : ''}`}><Heart className={`h-6 w-6 ${liked ? 'fill-[color:var(--alb-pink)]' : ''}`} /></span>
                    <span className="text-xs font-mono-tab mt-1">{formatViews(likes)}</span>
                </button>
                <button data-testid="shorts-comment-button" onClick={openComments} className="flex flex-col items-center text-white">
                    <span className="h-12 w-12 grid place-items-center rounded-full bg-black/40 backdrop-blur"><MessageCircle className="h-6 w-6" /></span>
                    <span className="text-xs font-mono-tab mt-1">{formatViews(commentsCount)}</span>
                </button>
                <button data-testid="shorts-share-button" onClick={share} className="flex flex-col items-center text-white">
                    <span className="h-12 w-12 grid place-items-center rounded-full bg-black/40 backdrop-blur"><Share2 className="h-6 w-6" /></span>
                    <span className="text-xs font-mono-tab mt-1">Comparteix</span>
                </button>
                <button onClick={toggleMute} className="flex flex-col items-center text-white">
                    <span className="h-12 w-12 grid place-items-center rounded-full bg-black/40 backdrop-blur">{muted ? <VolumeX className="h-6 w-6" /> : <Volume2 className="h-6 w-6" />}</span>
                </button>
            </div>

            <div className="absolute left-3 right-16 bottom-6 text-white">
                <div className="flex items-center gap-2 mb-2">
                    <Avatar className="h-8 w-8 ring-2 ring-[color:var(--alb-sun)]">
                        <AvatarImage src={video.uploader_picture} />
                        <AvatarFallback className="bg-[color:var(--alb-surface)] text-[color:var(--alb-sun)]">{(video.uploader_name || 'U').slice(0,1)}</AvatarFallback>
                    </Avatar>
                    <span className="font-semibold">{video.uploader_name}</span>
                    {user && user.user_id !== video.uploader_id && (
                        <button
                            type="button"
                            onClick={toggleFollow}
                            disabled={followBusy}
                            data-testid="shorts-follow-button"
                            className={`pointer-events-auto ml-1 inline-flex min-h-7 items-center gap-1 rounded-full border px-2.5 text-[11px] font-mono-tab transition-colors ${following ? 'border-white/30 text-white/80' : 'border-[color:var(--alb-sun)] text-[color:var(--alb-sun)] hover:bg-[color:var(--alb-sun)] hover:text-[color:var(--alb-ink)]'}`}
                        >
                            {following ? <UserMinus className="h-3 w-3" /> : <UserPlus className="h-3 w-3" />}
                            {following ? 'Seguint' : 'Seguir'}
                        </button>
                    )}
                </div>
                <h2 className="font-display text-[15px] leading-snug line-clamp-2">{video.title}</h2>
                {trailerTarget && (
                    <button
                        type="button"
                        onClick={() => { window.location.href = trailerTarget; }}
                        className="pointer-events-auto mt-3 inline-flex items-center gap-2 rounded bg-[color:var(--alb-sun)] px-3 py-2 text-sm font-semibold text-[color:var(--alb-ink)]"
                        data-testid="shorts-trailer-cta"
                    >
                        <ExternalLink className="h-4 w-4" /> Veure {video.trailer_for_video_id ? 'el vídeo' : 'la sèrie'}
                    </button>
                )}
            </div>

            {showComments && (
                <div className="absolute inset-x-0 bottom-0 z-20 flex max-h-[70%] flex-col rounded-t-2xl bg-[color:var(--alb-surface)] text-[color:var(--alb-text)]" data-testid="shorts-comments-panel">
                    <div className="flex items-center justify-between border-b border-[color:var(--alb-hairline)] px-4 py-3">
                        <span className="font-semibold">Comentaris ({formatViews(commentsCount)})</span>
                        <button type="button" onClick={() => setShowComments(false)} className="h-8 w-8 grid place-items-center rounded-full hover:bg-white/10"><X className="h-4 w-4" /></button>
                    </div>
                    <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                        {commentsLoading && <div className="text-sm text-[color:var(--alb-text-muted)]">Carregant...</div>}
                        {!commentsLoading && comments.length === 0 && <div className="text-sm text-[color:var(--alb-text-muted)]">Encara no hi ha comentaris.</div>}
                        {comments.map((c) => (
                            <div key={c.comment_id} className="flex items-start gap-2">
                                <Avatar className="h-7 w-7">
                                    <AvatarImage src={c.user_picture} />
                                    <AvatarFallback className="bg-[color:var(--alb-surface-2)] text-[color:var(--alb-sun)]">{(c.user_name || 'U').slice(0,1)}</AvatarFallback>
                                </Avatar>
                                <div className="min-w-0">
                                    <div className="text-sm font-semibold">{c.user_name}</div>
                                    <div className="text-sm whitespace-pre-wrap break-words">{c.content}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <form onSubmit={submitComment} className="flex items-center gap-2 border-t border-[color:var(--alb-hairline)] p-3">
                        <input
                            value={commentText}
                            onChange={(e) => setCommentText(e.target.value)}
                            placeholder="Escriu un comentari..."
                            className="flex-1 rounded-full bg-[color:var(--alb-ink-2)] border border-[color:var(--alb-hairline)] px-3 py-2 text-sm outline-none"
                            data-testid="shorts-comment-input"
                        />
                        <button type="submit" disabled={commentBusy || !commentText.trim()} className="h-9 w-9 grid place-items-center rounded-full bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)] disabled:opacity-50" data-testid="shorts-comment-submit"><Send className="h-4 w-4" /></button>
                    </form>
                </div>
            )}
        </div>
    );
}
